import React, { useState, useEffect } from 'react';
import PublicRoute from 'enums/publicRoutes';
import DefaultLayout from 'admin/components/DefaultLayout';
import { useIntl } from 'react-intl';
import {
  Button,
  Tabs,
  Table,
  DatePicker,
  Dropdown,
  Input,
  InputNumber,
  Modal,
  Icon,
  Spinner,
} from 'ui';
import Filter from 'admin/components/Filter';
import { StyledButtonList } from 'styles/buttonList';
import dayjs, { Dayjs } from 'dayjs';
import EnumSelect from '../../../Selects/EnumSelect';
import useQueryApiClient from '../../../../utils/useQueryApiClient';
import FileDownload from 'js-file-download';
import { MenuProps } from 'antd/es/menu';
import { Tooltip } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { TransactionTypeSelector } from '../../../../ui/Select/Selectors/TransactionTypeSelector';
import { PaymentStatusSelector } from '../../../../ui/Select/Selectors/PaymentStatusSelector';
import { Pagination } from '../../../../types/Pagination';
import { StyledPage, StyledTransactionFilterWrapper, TransactionTypeTag } from './style';
import useFarmOrderHandler from 'hooks/useFarmOrderHandler';
import { OrdersViewOrder } from '../../Orders/OrdersViewOrder/OrdersViewOrder';
import { useAuth } from '../../../../hooks/useAuth';
import { permissions } from '../../../../config/config';
import { useSearchParams } from 'react-router-dom';
import {
  StyledMobileHeaderWrapper,
  StyledMobilePageTitle,
} from 'admin/components/MobileLayout/style';
import MobileList from 'admin/components/MobileLayout/MobileList';
import AdminMobileFilterDrawer from 'ui/Drawer/Drawers/AdminMobileFilterDrawer';
import { useBreakpoint } from 'utils/useBreakpoint';
import { ResponsiveWrapper } from 'admin/components/ResponsiveWrapper';
import RatingDrawer from 'ui/Drawer/Drawers/RatingDrawer';
import CancelTransactionDrawer from 'ui/Drawer/Drawers/CancelTransactionDrawer';
import DrawerSendMessage from 'ui/Drawer/Drawers/DrawerSendMessage';
import MotherTransactionDrawer from 'ui/Drawer/Drawers/MotherTransactionDrawer';
import { Transaction } from '../../../../interfaces/BackendModels';

interface TransactionRow extends Transaction {
  has_rating?: boolean;
  transaction_type?: string;
  effective_transaction_type?: string;
  buyer_farm_name?: string;
  seller_farm_name?: string;
  delivery_type?: string;
  order_status?: string;
  horizon_href?: string;
  number?: string;
  created_date?: string;
  parent_transaction_id?: number | null;
  children_count?: number;
  children?: TransactionRow[];
}

interface Rating {
  id: number;
  transaction_id: number;
  product_quality_rating: number | null;
  delivery_rating: number | null;
  communication_rating: number | null;
  payments_rating: number | null;
  comments: string | null;
}

interface AvailableDate {
  year: number;
  month: number;
}

interface transaction {
  status_not: string[];
  year?: string;
  month?: string | number;
  sort?: Record<string, string>;
}

const TransactionListPage = () => {
  const [advancedSearch, setAdvancedSearch] = useState<transaction>({
    status_not: [],
    sort: { created_at: 'desc' },
  });

  const { handleDownloadClick } = useFarmOrderHandler();

  const [selectedTab, setSelectedTab] = useState<string>('transactions');
  const [isOnStatusChange, setIsOnStatusChange] = useState<boolean>(false);
  const [reloadPage, setReloadPage] = useState<number>(0);
  const [idsToCancel, setIdsToCancel] = useState<React.Key[]>([]);
  const [viewOrderData, setViewOrderData] = useState<Transaction | undefined>(
    undefined
  );
  const [isOpenViewOrderDrawer, setIsOpenViewOrderDrawer] =
    useState<boolean>(false);
  const [reloadFilter, setReloadFilter] = useState<number>(0);
  const [isCancelDrawerOpen, setIsCancelDrawerOpen] = useState(false);
  const [transactionToCancel, setTransactionToCancel] =
    useState<TransactionRow | null>(null);
  const [isMessageDrawerOpen, setIsMessageDrawerOpen] = useState(false);

  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [availableMonths, setAvailableMonths] = useState<number[]>([]);

  const [paginationStatus, setPaginationStatus] = useState<Pagination>({});
  const [staticFilter, setStaticFilter] = useState<transaction>({
    status_not: [],
  });
  const [isOldMobileFilterOpen, setIsOldMobileFilterOpen] =
    useState<boolean>(false);
  const [isActiveMobileFilterOpen, setIsActiveMobileFilterOpen] =
    useState<boolean>(false);
  const [isRatingDrawerOpen, setIsRatingDrawerOpen] = useState(false);
  const [currentRating, setCurrentRating] = useState<Rating | null>(null);
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionRow | null>(null);
  const [ratingViewOnly, setRatingViewOnly] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [childrenCache, setChildrenCache] = useState<
    Record<number, TransactionRow[]>
  >({});
  const [loadingChildren, setLoadingChildren] = useState<
    Record<number, boolean>
  >({});
  const [isMotherDrawerOpen, setIsMotherDrawerOpen] = useState(false);
  const [motherTransactionId, setMotherTransactionId] = useState<number | null>(
    null
  );
  const intl = useIntl();
  const { user, can } = useAuth();
  const canEditTransaction = can(permissions.edit_transaction);
  const { isLgUp } = useBreakpoint();

  let [searchParams] = useSearchParams();
  const transactionId = searchParams.get('transaction');
  const motherTransactionParam = searchParams.get('motherTransaction');
  const tabParam = searchParams.get('tab');

  useEffect(() => {
    if (!transactionId) return;

    setSelectedTab(tabParam || 'my_orders');
    fetchOrder({}, { transactionId: transactionId });
  }, [transactionId]);

  useEffect(() => {
    if (!motherTransactionParam) return;

    setSelectedTab(tabParam || 'my_orders');
    setMotherTransactionId(Number(motherTransactionParam));
    setIsMotherDrawerOpen(true);
  }, [motherTransactionParam]);

  const isAdmin = user?.isAdmin;

  const handleSuccessOrderFetch = (response: Transaction) => {
    setViewOrderData(response);
    setIsOpenViewOrderDrawer(true);
  };

  const handleCloseViewOrderDrawer = () => {
    setViewOrderData(undefined);
    setIsOpenViewOrderDrawer(false);
  };

  const openFilter = () => {
    if (selectedTab === 'transactions') {
      setIsActiveMobileFilterOpen(true);
    } else {
      setIsOldMobileFilterOpen(true);
    }
  };

  const closeFilters = () => {
    setIsActiveMobileFilterOpen(false);
    setIsOldMobileFilterOpen(false);
  };

  const resetFilter = () => {
    const defaultFilter = {
      status_not: [],
      sort: { created_at: 'desc' },
    };

    setStaticFilter({
      status_not: [],
    });

    setAdvancedSearch(defaultFilter);

    setReloadFilter((prev) => prev + 1);
  };

  const { appendData: exportData } = useQueryApiClient({
    request: {
      url: `api/v2/farm/transactions/export`,
      data: {
        filter: advancedSearch,
      },
      multipart: true,
      method: 'POST',
    },
    onSuccess: (response, format) => {
      FileDownload(
        response,
        `${format}_${dayjs().format('YYYY-MM-DD')}.${format}`
      );
    },
  });

  const { appendData: changeStatus } = useQueryApiClient({
    request: {
      url: `api/v2/transactions/change-status`,
      method: 'POST',
    },
    onSuccess: () => {
      setIsOnStatusChange(false);
      setReloadPage(reloadPage + 1);
      if (viewOrderData?.id) {
        fetchOrder({}, { transactionId: viewOrderData.id });
      }
    },
    onError: () => {
      setIsOnStatusChange(false);
    },
  });

  const { appendData: fetchOrder } = useQueryApiClient({
    request: {
      url: 'api/v2/transactions/:transactionId',
      disableOnMount: true,
    },
    onSuccess: (response) => handleSuccessOrderFetch(response),
  });

  const { appendData: fetchRating } = useQueryApiClient({
    request: {
      url: 'api/v2/transactions/:transactionId/rating',
      disableOnMount: true,
    },
    onSuccess: (response) => {
      setCurrentRating(response);
    },
  });

  const { appendData: fetchChildTransactions } = useQueryApiClient({
    request: {
      url: 'api/v2/transactions/:transactionId/children',
      disableOnMount: true,
    },
    onSuccess: (response: TransactionRow[], passOnSuccess?: number) => {
      if (passOnSuccess !== undefined) {
        setChildrenCache((prev) => ({ ...prev, [passOnSuccess]: response }));
        setLoadingChildren((prev) => ({ ...prev, [passOnSuccess]: false }));
      }
    },
  });

  const handleExpandedRowsChange = (expandedKeys: readonly React.Key[]) => {
    setExpandedRowKeys([...expandedKeys]);

    expandedKeys.forEach((key) => {
      const transactionId = Number(key);
      if (!childrenCache[transactionId] && !loadingChildren[transactionId]) {
        setLoadingChildren((prev) => ({ ...prev, [transactionId]: true }));
        fetchChildTransactions({}, { transactionId }, transactionId);
      }
    });
  };

  const handleOpenRatingDrawer = (
    transaction: TransactionRow,
    viewOnly: boolean = false
  ) => {
    setSelectedTransaction(transaction);
    setRatingViewOnly(viewOnly);
    fetchRating({}, { transactionId: transaction.id });
    setIsRatingDrawerOpen(true);
  };

  const handleCloseRatingDrawer = () => {
    setIsRatingDrawerOpen(false);
    setCurrentRating(null);
    setSelectedTransaction(null);
    setRatingViewOnly(false);
    setReloadPage(reloadPage + 1);
  };

  const handleOpenCancelDrawer = (id: string) => {
    const transaction = viewOrderData as TransactionRow;
    if (transaction) {
      setTransactionToCancel(transaction);
      setIsCancelDrawerOpen(true);
    }
  };

  const handleCloseCancelDrawer = () => {
    setIsCancelDrawerOpen(false);
    setTransactionToCancel(null);
  };

  const handleCancelSuccess = () => {
    setReloadPage(reloadPage + 1);
    if (viewOrderData?.id) {
      fetchOrder({}, { transactionId: viewOrderData.id });
    }
  };

  const handleOpenMessageDrawer = () => {
    setIsMessageDrawerOpen(true);
  };

  const handleCloseMessageDrawer = () => {
    setIsMessageDrawerOpen(false);
  };

  const handleOpenMotherDrawer = (transactionId: number) => {
    setMotherTransactionId(transactionId);
    setIsMotherDrawerOpen(true);
  };

  const handleCloseMotherDrawer = () => {
    setIsMotherDrawerOpen(false);
    setMotherTransactionId(null);
  };

  const handleOpenChildTransaction = (childId: number) => {
    fetchOrder({}, { transactionId: childId });
  };

  const transactionsColumns = [
    {
      title: (
        <div
          onClick={() =>
            setAdvancedSearch({
              ...advancedSearch,
              sort: {
                created_at:
                  advancedSearch?.sort?.created_at === 'asc' ? 'desc' : 'asc',
              },
            })
          }
        >
          {intl.formatMessage({ id: 'transactions.date_time' })}
          <span className="sort-arrows">
            <UpOutlined
              className={`sort-arrow sort-arrow-${
                advancedSearch?.sort?.created_at === 'asc'
                  ? 'active'
                  : 'inactive'
              }`}
            />
            <DownOutlined
              className={`sort-arrow sort-arrow-${
                advancedSearch?.sort?.created_at === 'desc'
                  ? 'active'
                  : 'inactive'
              }`}
            />
          </span>
        </div>
      ),
      dataIndex: 'created_date',
      render: (value: string) =>
        value && dayjs(value).format('DD.MM.YYYY | HH:mm'),
    },
    {
      title: intl.formatMessage({ id: 'transactions.transaction_nr' }),
      dataIndex: 'invoice_nr',
      render: (value: string) =>
        value || intl.formatMessage({ id: 'transactions.no_number' }),
    },
    {
      title: intl.formatMessage({ id: 'transactions.column.transaction_type' }),
      key: 'transaction_type',
      render: (record: TransactionRow) => {
        const type =
          record.effective_transaction_type ?? record.transaction_type;

        if (!type) {
          return (
            <TransactionTypeTag $tagType="emarket">
              {intl.formatMessage({ id: 'transactions.emarket_transaction' })}
            </TransactionTypeTag>
          );
        }
        if (type === 'order') {
          return (
            <TransactionTypeTag $tagType="wholesale">
              {intl.formatMessage({ id: 'transactions.wholesale_transaction' })}
            </TransactionTypeTag>
          );
        }
        const tagTypeMap: Record<
          string,
          'motherPurchase' | 'motherReservation' | 'motherDynamicPurchase'
        > = {
          purchase: 'motherPurchase',
          reservation: 'motherReservation',
          dynamic_purchase: 'motherDynamicPurchase',
        };
        const tagType = tagTypeMap[type] ?? 'motherOrder';
        const labelId = `transactions.mother_${type}_transaction`;
        return (
          <TransactionTypeTag $tagType={tagType}>
            {intl.formatMessage({ id: labelId })}
          </TransactionTypeTag>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'transactions.column.client' }),
      key: 'client',
      render: (record: TransactionRow) =>
        record.buyer_farm_name || record.customer_name || '-',
    },
    {
      title: intl.formatMessage({ id: 'transactions.delivery_type' }),
      key: 'delivery_type',
      render: (record: TransactionRow) =>
        record.delivery_type
          ? intl.formatMessage({
              id: `enum.${record.delivery_type.toUpperCase()}`,
            })
          : '-',
    },
    {
      title: intl.formatMessage({ id: 'transactions.status' }),
      dataIndex: 'status',
      render: (value: string) =>
        value && intl.formatMessage({ id: 'enum.' + value?.toUpperCase() }),
    },
    {
      title: intl.formatMessage({ id: 'transactions.price' }),
      key: 'amount',
      render: (record: TransactionRow) => {
        const type =
          record.effective_transaction_type ?? record.transaction_type;
        const isEMarket = !type;
        const isPaid = isEMarket || record.billing_status === 1;
        const amount = Number(record.total_sum || 0).toFixed(2);
        return (
          <div className="price-cell">
            <span className="price-amount">{amount} EUR</span>
            <Tooltip
              title={intl.formatMessage({
                id: isPaid
                  ? 'transactions.payment_status.paid'
                  : 'transactions.payment_status.unpaid',
              })}
              getPopupContainer={(triggerNode) => triggerNode}
            >
              <span className="payment-tooltip-wrapper">
                {isPaid ? (
                  <CheckCircleFilled className="payment-status-icon paid" />
                ) : (
                  <CloseCircleFilled className="payment-status-icon unpaid" />
                )}
              </span>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'transactions.invoice' }),
      key: 'invoice',
      render: (record: TransactionRow) => {
        const isWholesale = [
          'order',
          'purchase',
          'reservation',
          'dynamic_purchase',
        ].includes(record.transaction_type?.toLowerCase() ?? '');

        if (isWholesale) {
          return '-';
        }

        if (record.horizon_href) {
          return (
            <a
              href={record.horizon_href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {intl.formatMessage({ id: 'transactions.action.download' })}
            </a>
          );
        }
        if (record.order_id && record.farm_id) {
          return (
            <span
              className="order-download"
              onClick={(ev: React.MouseEvent) => {
                ev.stopPropagation();
                handleDownloadClick([
                  {
                    order_id: String(record.order_id),
                    farm_id: record.farm_id,
                  },
                ]);
              }}
            >
              <span className="transactions-download">
                {intl.formatMessage({ id: 'transactions.download' })}
              </span>
            </span>
          );
        }
        return '-';
      },
    },
  ];

  const myOrdersColumns = [
    {
      title: (
        <div
          onClick={() =>
            setAdvancedSearch({
              ...advancedSearch,
              sort: {
                created_at:
                  advancedSearch?.sort?.created_at === 'asc' ? 'desc' : 'asc',
              },
            })
          }
        >
          {intl.formatMessage({ id: 'transactions.date_time' })}
          <span className="sort-arrows">
            <UpOutlined
              className={`sort-arrow sort-arrow-${
                advancedSearch?.sort?.created_at === 'asc'
                  ? 'active'
                  : 'inactive'
              }`}
            />
            <DownOutlined
              className={`sort-arrow sort-arrow-${
                advancedSearch?.sort?.created_at === 'desc'
                  ? 'active'
                  : 'inactive'
              }`}
            />
          </span>
        </div>
      ),
      dataIndex: 'created_date',
      width: '15%',
      render: (value: string) =>
        value && dayjs(value).format('DD.MM.YYYY | HH:mm'),
    },
    {
      title: intl.formatMessage({ id: 'transactions.order_nr' }),
      dataIndex: 'invoice_nr',
      width: '12%',
      render: (value: string) =>
        value || intl.formatMessage({ id: 'transactions.no_number' }),
    },
    {
      title: intl.formatMessage({ id: 'transactions.column.transaction_type' }),
      key: 'transaction_type',
      width: '15%',
      render: (record: TransactionRow) => {
        const isParent = (record.children_count ?? 0) > 0;
        if (!isParent) {
          if (!record.transaction_type) {
            return (
              <TransactionTypeTag $tagType="emarket">
                {intl.formatMessage({
                  id: 'transactions.emarket_order_transaction',
                })}
              </TransactionTypeTag>
            );
          }
          return (
            <TransactionTypeTag $tagType="order">
              {intl.formatMessage({ id: 'transactions.order_transaction' })}
            </TransactionTypeTag>
          );
        }
        const tagTypeMap: Record<
          string,
          'motherPurchase' | 'motherReservation' | 'motherDynamicPurchase'
        > = {
          purchase: 'motherPurchase',
          reservation: 'motherReservation',
          dynamic_purchase: 'motherDynamicPurchase',
        };
        const tagType = record.transaction_type
          ? (tagTypeMap[record.transaction_type] ?? 'motherOrder')
          : 'motherOrder';
        const labelId = record.transaction_type
          ? `transactions.mother_${record.transaction_type}_transaction`
          : 'transactions.mother_order_transaction';
        return (
          <TransactionTypeTag $tagType={tagType}>
            {intl.formatMessage({ id: labelId })}
          </TransactionTypeTag>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'transactions.column.supplier' }),
      key: 'supplier',
      width: '12%',
      render: (record: TransactionRow) => {
        const isParent = (record.children_count ?? 0) > 0;
        return isParent ? '' : record.seller_farm_name || '-';
      },
    },
    {
      title: intl.formatMessage({ id: 'transactions.delivery_type' }),
      key: 'delivery_type',
      width: '12%',
      render: (record: TransactionRow) => {
        const isParent = (record.children_count ?? 0) > 0;
        if (isParent) return '';
        return record.delivery_type
          ? intl.formatMessage({
              id: `enum.${record.delivery_type.toUpperCase()}`,
            })
          : '-';
      },
    },
    {
      title: intl.formatMessage({ id: 'transactions.status' }),
      dataIndex: 'status',
      width: '12%',
      render: (value: string) =>
        value && intl.formatMessage({ id: 'enum.' + value?.toUpperCase() }),
    },
    {
      title: intl.formatMessage({ id: 'transactions.price' }),
      key: 'amount',
      width: '12%',
      render: (record: TransactionRow) => {
        const type =
          record.effective_transaction_type ?? record.transaction_type;
        const isEMarket = !type;
        const isPaid = isEMarket || record.billing_status === 1;
        const amount = Number(record.total_sum || 0).toFixed(2);
        return (
          <div className="price-cell">
            <span className="price-amount">{amount} EUR</span>
            <Tooltip
              title={intl.formatMessage({
                id: isPaid
                  ? 'transactions.payment_status.paid'
                  : 'transactions.payment_status.unpaid',
              })}
              getPopupContainer={(triggerNode) => triggerNode}
            >
              <span className="payment-tooltip-wrapper">
                {isPaid ? (
                  <CheckCircleFilled className="payment-status-icon paid" />
                ) : (
                  <CloseCircleFilled className="payment-status-icon unpaid" />
                )}
              </span>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'transactions.invoice' }),
      key: 'invoice',
      width: '10%',
      render: (record: TransactionRow) => {
        const isParent = (record.children_count ?? 0) > 0;
        if (isParent) return '';
        const isPostPayment = [
          'order',
          'purchase',
          'reservation',
          'dynamic_purchase',
        ].includes(record.transaction_type?.toLowerCase() ?? '');
        if (isPostPayment) return '-';
        if (record.horizon_href) {
          return (
            <a
              href={record.horizon_href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {intl.formatMessage({ id: 'transactions.action.download' })}
            </a>
          );
        }
        if (record.order_id && record.farm_id) {
          return (
            <span
              className="order-download"
              onClick={(ev: React.MouseEvent) => {
                ev.stopPropagation();
                handleDownloadClick([
                  {
                    order_id: String(record.order_id),
                    farm_id: record.farm_id,
                  },
                ]);
              }}
            >
              <span className="transactions-download">
                {intl.formatMessage({ id: 'transactions.download' })}
              </span>
            </span>
          );
        }
        return '-';
      },
    },
  ];

  const { refetch } = useQueryApiClient({
    request: {
      url: `api/v2/farm/transactions/available-dates`,
      data: {
        filter: advancedSearch,
      },
    },
    onSuccess: (response: AvailableDate[]) => {
      setAvailableYears(
        Array.from(new Set(response.map((item) => Number(item.year))))
      );
      setAvailableMonths(
        Array.from(new Set(response.map((item) => Number(item.month))))
      );
    },
  });

  useEffect(() => {
    refetch();
  }, [selectedTab]);

  const disabledYear = (current: Dayjs) => {
    const year = current.year();

    return !availableYears.includes(year);
  };

  const disabledMonth = (current: Dayjs) => {
    const month = current.month() + 1;

    return !availableMonths.includes(month);
  };

  const oldTransactionFilter = (
    <Filter
      onFinish={setAdvancedSearch}
      totalRecord={paginationStatus?.total ?? 0}
      staticFilter={staticFilter}
      customParse={{
        year: (value: Dayjs) => value?.year(),
        month: (value: Dayjs) => value?.format('MMMM'),
      }}
      defaultFilterParam={advancedSearch}
      isLabelVisible={true}
      reloadFilterTag={reloadFilter}
      tagEnumPrefixes={{
        transaction_type: 'my.orders.transaction_type',
        payment_status: 'transactions.payment_status',
      }}
    >
      <DatePicker
        label={intl.formatMessage({ id: 'general.year' })}
        name="year"
        picker="year"
        format="YYYY"
        disabledDate={disabledYear}
      />
      <DatePicker
        label={intl.formatMessage({ id: 'general.month' })}
        name="month"
        picker="month"
        format="MMMM"
        disabledDate={disabledMonth}
        className="month-picker"
      />
      <TransactionTypeSelector
        includeEmarket
        name="transaction_type"
        customLabels={{ order: 'transactions.order_transaction' }}
        style={{ maxWidth: 160 }}
      />
      <PaymentStatusSelector name="payment_status" style={{ maxWidth: 140 }} />
      <EnumSelect
        label={intl.formatMessage({ id: 'transactions.status' })}
        name="status"
        code="OrderTransactionStatusType"
        allowClear
        style={{ minWidth: 180 }}
      />
      <Input
        label={intl.formatMessage({ id: 'general.search' })}
        name="search"
        allowClear
      />
      <InputNumber
        label={intl.formatMessage({ id: 'transactions.price' })}
        name="price"
        precision={2}
      />
      <div className={'button-section-container'}>
        <div className={'button-section-wrapper'}>
          <Button
            className={'mobile-button'}
            type="link"
            label={intl.formatMessage({ id: 'general.delete_filter' })}
            onClick={() => resetFilter()}
          />

          <Button
            className={'mobile-button'}
            label={intl.formatMessage({ id: 'general.cancel' })}
            onClick={() => closeFilters()}
          />

          <Button
            type="primary"
            label={
              isLgUp
                ? intl.formatMessage({ id: 'transaction.general.select' })
                : intl.formatMessage({ id: 'general.filter_action' })
            }
            htmlType="submit"
            onClick={() => closeFilters()}
          />
        </div>
      </div>
    </Filter>
  );

  const activeTransactionFilter = (
    <Filter
      onFinish={setAdvancedSearch}
      totalRecord={paginationStatus?.total ?? 0}
      staticFilter={staticFilter}
      customParse={{
        year: (value: Dayjs) => value?.year(),
        month: (value: Dayjs) => value?.format('MMMM'),
      }}
      isLabelVisible={true}
      defaultFilterParam={advancedSearch}
      reloadFilterTag={reloadFilter}
      tagEnumPrefixes={{ payment_status: 'transactions.payment_status' }}
    >
      <DatePicker
        label={intl.formatMessage({ id: 'general.year' })}
        name="year"
        picker="year"
        format="YYYY"
        disabledDate={disabledYear}
      />
      <DatePicker
        label={intl.formatMessage({ id: 'general.month' })}
        name="month"
        picker="month"
        format="MMMM"
        disabledDate={disabledMonth}
        className="month-picker"
      />
      <TransactionTypeSelector
        includeEmarket
        name="transaction_type"
        customLabels={{ 'e-market': 'transactions.emarket_transaction' }}
        style={{ maxWidth: 160 }}
      />
      <PaymentStatusSelector name="payment_status" style={{ maxWidth: 140 }} />
      <EnumSelect
        label={intl.formatMessage({ id: 'transactions.status' })}
        name="status"
        code="OrderTransactionStatusType"
        limited={[
          'new',
          'confirmed',
          'pending_send',
          'partially_sent',
          'sent',
          'done',
          'canceled',
        ]}
        allowClear
        style={{ minWidth: 180 }}
      />
      <Input
        label={intl.formatMessage({ id: 'general.search' })}
        name="search"
        allowClear
      />
      <InputNumber
        label={intl.formatMessage({ id: 'transactions.price' })}
        name="price"
        precision={2}
      />
      <div className={'button-section-container'}>
        <div className={'button-section-wrapper'}>
          <Button
            className={'mobile-button'}
            type="link"
            label={intl.formatMessage({ id: 'general.delete_filter' })}
            onClick={() => resetFilter()}
          />

          <Button
            className={'mobile-button'}
            label={intl.formatMessage({ id: 'general.cancel' })}
            onClick={() => closeFilters()}
          />

          <Button
            type="primary"
            label={
              isLgUp
                ? intl.formatMessage({ id: 'transaction.general.select' })
                : intl.formatMessage({ id: 'general.filter_action' })
            }
            htmlType="submit"
            onClick={() => closeFilters()}
          />
        </div>
      </div>
    </Filter>
  );

  const getTransactionTypeTag = (record: TransactionRow) => {
    const type =
      record.effective_transaction_type ?? record.transaction_type;
    if (!type) {
      return (
        <TransactionTypeTag $tagType="emarket">
          {intl.formatMessage({ id: 'transactions.emarket_transaction' })}
        </TransactionTypeTag>
      );
    }
    if (type === 'order') {
      return (
        <TransactionTypeTag $tagType="wholesale">
          {intl.formatMessage({ id: 'transactions.wholesale_transaction' })}
        </TransactionTypeTag>
      );
    }
    const tagTypeMap: Record<
      string,
      'motherPurchase' | 'motherReservation' | 'motherDynamicPurchase'
    > = {
      purchase: 'motherPurchase',
      reservation: 'motherReservation',
      dynamic_purchase: 'motherDynamicPurchase',
    };
    const tagType = tagTypeMap[type] ?? 'motherOrder';
    const labelId = `transactions.mother_${type}_transaction`;
    return (
      <TransactionTypeTag $tagType={tagType}>
        {intl.formatMessage({ id: labelId })}
      </TransactionTypeTag>
    );
  };

  const getMyOrdersTypeTag = (record: TransactionRow) => {
    const isParent = (record.children_count ?? 0) > 0;
    if (!isParent) {
      if (!record.transaction_type) {
        return (
          <TransactionTypeTag $tagType="emarket">
            {intl.formatMessage({
              id: 'transactions.emarket_order_transaction',
            })}
          </TransactionTypeTag>
        );
      }
      return (
        <TransactionTypeTag $tagType="order">
          {intl.formatMessage({ id: 'transactions.order_transaction' })}
        </TransactionTypeTag>
      );
    }
    const tagTypeMap: Record<
      string,
      'motherPurchase' | 'motherReservation' | 'motherDynamicPurchase'
    > = {
      purchase: 'motherPurchase',
      reservation: 'motherReservation',
      dynamic_purchase: 'motherDynamicPurchase',
    };
    const type = record.transaction_type ?? '';
    const tagType = tagTypeMap[type] ?? 'motherOrder';
    const labelId = `transactions.mother_${type}_transaction`;
    return (
      <TransactionTypeTag $tagType={tagType}>
        {intl.formatMessage({ id: labelId })}
      </TransactionTypeTag>
    );
  };

  const renderInvoiceLink = (record: TransactionRow) => {
    const isWholesale = [
      'order',
      'purchase',
      'reservation',
      'dynamic_purchase',
    ].includes(record.transaction_type?.toLowerCase() ?? '');
    if (isWholesale) return null;
    if (record.horizon_href) {
      return (
        <a
          href={record.horizon_href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {intl.formatMessage({ id: 'transactions.download' })}
        </a>
      );
    }
    if (record.order_id && record.farm_id) {
      return (
        <span
          className="order-download"
          onClick={(ev: React.MouseEvent) => {
            ev.stopPropagation();
            handleDownloadClick([
              {
                order_id: String(record.order_id),
                farm_id: String(record.farm_id),
              },
            ]);
          }}
        >
          {intl.formatMessage({ id: 'transactions.download' })}
        </span>
      );
    }
    return '-';
  };

  const transactionsMobileItems = {
    coverFields: [
      {
        name: 'invoice_nr',
        render: (value: string, record: TransactionRow) => (
          <div>
            <div>
              {value ||
                intl.formatMessage({ id: 'transactions.no_number' })}
            </div>
            <div>
              {record.created_date &&
                dayjs(record.created_date).format('DD.MM.YYYY')}
            </div>
          </div>
        ),
      },
      {
        name: 'transaction_type',
        render: (_value: string, record: TransactionRow) =>
          getTransactionTypeTag(record),
      },
    ],
    bodyFields: [
      {
        name: 'customer_name',
        label: intl.formatMessage({ id: 'transactions.customer' }),
        render: (_value: string, record: TransactionRow) =>
          record.buyer_farm_name || record.customer_name || '-',
      },
      {
        name: 'delivery_type',
        label: intl.formatMessage({ id: 'transactions.delivery_type' }),
        render: (value: string) =>
          value
            ? intl.formatMessage({ id: `enum.${value.toUpperCase()}` })
            : '-',
      },
      {
        name: 'status',
        label: intl.formatMessage({ id: 'transactions.status' }),
        render: (value: string) =>
          value &&
          intl.formatMessage({ id: 'enum.' + value?.toUpperCase() }),
      },
      {
        name: 'total_sum',
        label: intl.formatMessage({ id: 'transactions.price' }),
        itemClassName: 'transaction-total',
        render: (_value: string, record: TransactionRow) => {
          const amount = Number(record.total_sum || 0).toFixed(2);
          const type =
            record.effective_transaction_type ?? record.transaction_type;
          const isEMarket = !type;
          const isPaid = isEMarket || record.billing_status === 1;
          const paymentLabel = intl.formatMessage({
            id: isPaid
              ? 'transactions.payment_status.paid'
              : 'transactions.payment_status.unpaid',
          });
          return `${amount} (${paymentLabel})`;
        },
      },
      {
        name: 'id',
        label: intl.formatMessage({ id: 'transactions.invoice' }),
        render: (_value: number, record: TransactionRow) =>
          renderInvoiceLink(record),
      },
    ],
  };

  const myOrdersMobileItems = {
    coverFields: [
      {
        name: 'invoice_nr',
        render: (value: string, record: TransactionRow) => (
          <div>
            <div>
              {value ||
                intl.formatMessage({ id: 'transactions.no_number' })}
            </div>
            <div>
              {record.created_date &&
                dayjs(record.created_date).format('DD.MM.YYYY')}
            </div>
          </div>
        ),
      },
      {
        name: 'transaction_type',
        render: (_value: string, record: TransactionRow) =>
          getMyOrdersTypeTag(record),
      },
    ],
    bodyFields: [
      {
        name: 'seller_farm_name',
        label: intl.formatMessage({ id: 'transactions.column.supplier' }),
        render: (_value: string, record: TransactionRow) => {
          const isParent = (record.children_count ?? 0) > 0;
          return isParent ? null : record.seller_farm_name || '-';
        },
      },
      {
        name: 'delivery_type',
        label: intl.formatMessage({ id: 'transactions.delivery_type' }),
        render: (value: string, record: TransactionRow) => {
          const isParent = (record.children_count ?? 0) > 0;
          if (isParent) return null;
          return value
            ? intl.formatMessage({ id: `enum.${value.toUpperCase()}` })
            : '-';
        },
      },
      {
        name: 'status',
        label: intl.formatMessage({ id: 'transactions.status' }),
        render: (value: string) =>
          value &&
          intl.formatMessage({ id: 'enum.' + value?.toUpperCase() }),
      },
      {
        name: 'total_sum',
        label: intl.formatMessage({ id: 'transactions.price' }),
        itemClassName: 'transaction-total',
        render: (_value: string, record: TransactionRow) =>
          `${Number(record.total_sum || 0).toFixed(2)}`,
      },
      {
        name: 'id',
        label: intl.formatMessage({ id: 'transactions.invoice' }),
        render: (_value: number, record: TransactionRow) => {
          const isParent = (record.children_count ?? 0) > 0;
          if (isParent) return null;
          return renderInvoiceLink(record);
        },
      },
    ],
  };

  const items = [
    {
      key: 'transactions',
      label: intl.formatMessage({ id: 'transactions.tab.transactions' }),
      children: (
        <ResponsiveWrapper
          desktopComponent={
            <>
              {activeTransactionFilter}
              <Table
                url={'/api/v2/farm/transactions'}
                columns={transactionsColumns}
                filter={advancedSearch}
                paginnationStatus={(el: object) => {
                  setPaginationStatus(el);
                }}
                loading={isOnStatusChange}
                reload={reloadPage}
                rowKey={'id'}
                onRow={(record: TransactionRow) => ({
                  onClick: () => {
                    fetchOrder({}, { transactionId: record.id });
                  },
                })}
                rowClassName="is-clickable"
              />
            </>
          }
          mobileComponent={
            <MobileList
              url={'/api/v2/farm/transactions'}
              filter={advancedSearch}
              paginnationStatus={(el: object) => {
                setPaginationStatus(el);
              }}
              reload={reloadPage}
              coverFields={transactionsMobileItems.coverFields}
              bodyFields={transactionsMobileItems.bodyFields}
              onItemClick={(record: TransactionRow) => {
                fetchOrder({}, { transactionId: record.id });
              }}
            />
          }
        />
      ),
    },
    {
      key: 'my_orders',
      label: intl.formatMessage({ id: 'transactions.tab.my_orders' }),
      children: (
        <ResponsiveWrapper
          desktopComponent={
            <>
              {oldTransactionFilter}
              <Table
                url="/api/v2/farm/transactions/my-orders"
                columns={myOrdersColumns}
                filter={advancedSearch}
                paginnationStatus={(el: object) => {
                  setPaginationStatus(el);
                }}
                loading={false}
                onRow={(record: TransactionRow) => ({
                  onClick: (e: React.MouseEvent) => {
                    const target = e.target as HTMLElement;
                    if (target.closest('.ant-table-row-expand-icon')) {
                      return;
                    }
                    const isParentTransaction =
                      (record.children_count ?? 0) > 0 && !record.farm_id;
                    if (isParentTransaction) {
                      handleOpenMotherDrawer(record.id);
                    } else {
                      fetchOrder({}, { transactionId: record.id });
                    }
                  },
                })}
                rowClassName={(record) => {
                  const isParent =
                    ((record as TransactionRow).children_count ?? 0) > 0;
                  const isExpanded = expandedRowKeys.includes(record.id);
                  return `is-clickable ${isParent ? 'parent-transaction-row' : ''} ${isParent && isExpanded ? 'expanded-row' : ''}`;
                }}
                rowExpandable={(record: TransactionRow) =>
                  (record.children_count ?? 0) > 0
                }
                expandedRowKeys={expandedRowKeys}
                onExpandedRowsChange={handleExpandedRowsChange}
                expandedRowRender={(record: TransactionRow) => {
                  const children = childrenCache[record.id];
                  const isLoading = loadingChildren[record.id];

                  if (isLoading) {
                    return (
                      <div className="child-transactions-container">
                        <Spinner spinning={true}>
                          <div className="child-loading-placeholder" />
                        </Spinner>
                      </div>
                    );
                  }

                  if (!children || children.length === 0) {
                    return null;
                  }

                  return (
                    <div className="child-transactions-container">
                      {children.map((child: TransactionRow) => (
                        <div
                          key={child.id}
                          className="child-transaction-row"
                          onClick={() =>
                            fetchOrder({}, { transactionId: child.id })
                          }
                        >
                          <span className="child-spacer" />
                          <span className="child-cell child-date">
                            {child.created_date &&
                              dayjs(child.created_date).format(
                                'DD.MM.YYYY | HH:mm'
                              )}
                          </span>
                          <span className="child-cell child-number">
                            {child.invoice_nr}
                          </span>
                          <span className="child-cell child-type">
                            <TransactionTypeTag $tagType="order">
                              {intl.formatMessage({
                                id: 'transactions.order_transaction',
                              })}
                            </TransactionTypeTag>
                          </span>
                          <span className="child-cell child-supplier">
                            {child.seller_farm_name || '-'}
                          </span>
                          <span className="child-cell child-delivery">
                            {child.delivery_type
                              ? intl.formatMessage({
                                  id: `enum.${child.delivery_type.toUpperCase()}`,
                                })
                              : '-'}
                          </span>
                          <span className="child-cell child-status">
                            {child.status &&
                              intl.formatMessage({
                                id: 'enum.' + child.status?.toUpperCase(),
                              })}
                          </span>
                          <span className="child-cell child-price">
                            {Number(child.total_sum || 0).toFixed(2)} EUR
                          </span>
                          <span className="child-cell child-invoice">
                            {(() => {
                              const isPostPayment = [
                                'order',
                                'purchase',
                                'reservation',
                                'dynamic_purchase',
                              ].includes(
                                child.transaction_type?.toLowerCase() ?? ''
                              );
                              if (isPostPayment) return '-';
                              return child.horizon_href ? (
                                <a
                                  href={child.horizon_href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {intl.formatMessage({
                                    id: 'transactions.action.download',
                                  })}
                                </a>
                              ) : child.order_id && child.farm_id ? (
                                <span
                                  className="order-download"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadClick([
                                      {
                                        order_id: String(child.order_id),
                                        farm_id: child.farm_id,
                                      },
                                    ]);
                                  }}
                                >
                                  <span className="transactions-download">
                                    {intl.formatMessage({
                                      id: 'transactions.download',
                                    })}
                                  </span>
                                </span>
                              ) : (
                                '-'
                              );
                            })()}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
            </>
          }
          mobileComponent={
            <>
              <MobileList
                url={'/api/v2/farm/transactions/my-orders'}
                filter={advancedSearch}
                paginnationStatus={(el: object) => {
                  setPaginationStatus(el);
                }}
                reload={reloadPage}
                coverFields={myOrdersMobileItems.coverFields}
                bodyFields={myOrdersMobileItems.bodyFields}
                onItemClick={(record: TransactionRow) => {
                  const isParentTransaction =
                    (record.children_count ?? 0) > 0 && !record.farm_id;
                  if (isParentTransaction) {
                    handleOpenMotherDrawer(record.id);
                  } else {
                    fetchOrder({}, { transactionId: record.id });
                  }
                }}
                rowClassName={(record: TransactionRow) =>
                  (record.children_count ?? 0) > 0
                    ? 'parent-transaction-card'
                    : ''
                }
              />
            </>
          }
        />
      ),
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    if (e.key === 'xlsx' || e.key === 'csv') {
      exportData({ format: e.key, ...advancedSearch }, [], e.key);
    }
  };

  const dropdownItems: MenuProps['items'] = [
    {
      label: intl.formatMessage({ id: 'general.excel' }),
      key: 'xlsx',
    },
  ];

  const menuProps = {
    items: dropdownItems,
    onClick: handleMenuClick,
  };

  return (
    <>
      <StyledMobileHeaderWrapper>
        <div className="mobile-header-item-wrapper" />
        <Button
          type={'text'}
          label={intl.formatMessage({ id: 'general.filter' })}
          onClick={() => openFilter()}
          icon={
            <Icon
              name={'admin-filter-icon'}
              className="admin-header-item-icon"
            />
          }
        />
      </StyledMobileHeaderWrapper>
      <DefaultLayout.PageLayout
        breadcrumbs={[
          {
            title: (
              <a href={PublicRoute.HOME}>
                {intl.formatMessage({ id: 'navigation.beginning' })}
              </a>
            ),
          },
          {
            title: intl.formatMessage({ id: `transactions.${selectedTab}` }),
          },
        ]}
      >
        <DefaultLayout.PageContent>
          <StyledPage>
            <StyledMobilePageTitle>
              <span>{intl.formatMessage({ id: 'general.transactions' })}</span>
            </StyledMobilePageTitle>
            <Tabs
              activeKey={selectedTab}
              destroyInactiveTabPane={true}
              tabBarExtraContent={
                <StyledButtonList className={'hide-mobile'}>
                  <Dropdown menu={menuProps}>
                    <Button
                      label={intl.formatMessage({ id: 'general.export' })}
                      type="primary"
                    />
                  </Dropdown>
                </StyledButtonList>
              }
              items={items}
              onChange={(key) => {
                setStaticFilter({
                  status_not: [],
                });
                setAdvancedSearch({
                  status_not: [],
                  sort: { created_at: 'desc' },
                });
                setReloadFilter(reloadFilter + 1);
                setSelectedTab(key);
              }}
            />
            <Modal
              open={idsToCancel.length > 0}
              onCancel={() => setIdsToCancel([])}
              destroyOnClose
              zIndex={1200}
              footer={[
                <Button
                  key="yes"
                  type="primary"
                  onClick={() => {
                    setIsOnStatusChange(true);

                    changeStatus({
                      status: 'canceled',
                      id: idsToCancel[0],
                    });

                    setIdsToCancel([]);
                  }}
                  label={intl.formatMessage({ id: 'general.yes' })}
                />,
                <Button
                  key="no"
                  type="primary"
                  onClick={() => setIdsToCancel([])}
                  label={intl.formatMessage({ id: 'general.no' })}
                />,
              ]}
            >
              <h3>
                {intl.formatMessage({ id: 'general.cancel_purchase_info' })}
              </h3>
              <p>
                {intl.formatMessage({ id: 'general.transactions_warning' })}
              </p>
            </Modal>
            <OrdersViewOrder
              open={isOpenViewOrderDrawer}
              onClose={handleCloseViewOrderDrawer}
              transaction={viewOrderData}
              refetchTransaction={fetchOrder}
              showActionButton={canEditTransaction}
              selectedTab={selectedTab}
              onStatusChange={(id: string, status: string) => {
                setIsOnStatusChange(true);
                changeStatus({ id, status });
              }}
              onCancel={handleOpenCancelDrawer}
              onOpenRatingDrawer={handleOpenRatingDrawer}
              onOpenMessageDrawer={handleOpenMessageDrawer}
              reloadPage={reloadPage}
              setReload={setReloadPage}
              isAdmin={isAdmin}
            />
          </StyledPage>

          <AdminMobileFilterDrawer
            isOpen={isOldMobileFilterOpen}
            onClose={() => closeFilters()}
          >
            <StyledTransactionFilterWrapper>
              {oldTransactionFilter}
            </StyledTransactionFilterWrapper>
          </AdminMobileFilterDrawer>
          <AdminMobileFilterDrawer
            isOpen={isActiveMobileFilterOpen}
            onClose={() => closeFilters()}
          >
            <StyledTransactionFilterWrapper>
              {activeTransactionFilter}
            </StyledTransactionFilterWrapper>
          </AdminMobileFilterDrawer>
          {selectedTransaction && (
            <RatingDrawer
              isOpen={isRatingDrawerOpen}
              onClose={handleCloseRatingDrawer}
              transactionId={selectedTransaction.id}
              transactionStatus={selectedTransaction.status}
              existingRating={currentRating}
              viewOnly={ratingViewOnly}
            />
          )}
          {transactionToCancel && (
            <CancelTransactionDrawer
              isOpen={isCancelDrawerOpen}
              onClose={handleCloseCancelDrawer}
              transactionId={transactionToCancel.id}
              transactionType={transactionToCancel.transaction_type}
              isCustomer={selectedTab === 'my_orders'}
              onSuccess={handleCancelSuccess}
            />
          )}
          <DrawerSendMessage
            open={isMessageDrawerOpen}
            onClose={handleCloseMessageDrawer}
            context={
              viewOrderData
                ? {
                    type: 'transaction',
                    transactionId: viewOrderData.id,
                    transactionType:
                      (viewOrderData as TransactionRow)
                        .effective_transaction_type ??
                      viewOrderData.transaction_type ??
                      undefined,
                    invoiceNr: viewOrderData.invoice_nr ?? '',
                  }
                : undefined
            }
          />
          {motherTransactionId && (
            <MotherTransactionDrawer
              isOpen={isMotherDrawerOpen}
              onClose={handleCloseMotherDrawer}
              transactionId={motherTransactionId}
              onOpenChildTransaction={handleOpenChildTransaction}
            />
          )}
        </DefaultLayout.PageContent>
      </DefaultLayout.PageLayout>
    </>
  );
};

export default TransactionListPage;
